import React from 'react';
import { Box } from '@mui/material';

const isWithinSantaWindow = (now = new Date()) => {
    // Local time window: Dec 21..Dec 25 (inclusive), every year
    const month = now.getMonth(); // 0=Jan ... 11=Dec
    const day = now.getDate(); // 1..31
    return month === 11 && day >= 21 && day <= 25;
};

interface SantaProps {
    intervalMs?: number;
    minDurationMs?: number;
    maxDurationMs?: number;
    debug?: boolean;
}

interface SantaRun {
    id: string;
    durationMs: number;
    direction: 'rtl' | 'ltr';
}

export const Santa = ({
    intervalMs = 140000,
    minDurationMs = 8000,
    maxDurationMs = 20000,
    debug = false
}: SantaProps) => {
    const [run, setRun] = React.useState<SantaRun | null>(null);

    const scheduleRun = React.useCallback(() => {
        const durationMs = Math.floor(Math.random() * (maxDurationMs - minDurationMs + 1)) + minDurationMs;

        const direction: SantaRun['direction'] = Math.random() < 0.5 ? 'rtl' : 'ltr';

        setRun({
            id: `${Date.now()}-${Math.random()}`,
            durationMs,
            direction
        });
    }, [maxDurationMs, minDurationMs]);

    const enabled = debug || isWithinSantaWindow();

    React.useEffect(() => {
        if (!enabled) {
            setRun(null);
            return;
        }

        // Debug: run continuously (start immediately; next runs are chained on animation end)
        if (debug) {
            scheduleRun();
            return;
        }

        // Normal: start after initial interval, then every interval
        const timer = window.setInterval(() => {
            scheduleRun();
        }, intervalMs);

        return () => {
            window.clearInterval(timer);
        };
    }, [debug, enabled, intervalMs, scheduleRun]);

    if (!enabled || !run) return null;

    const isRTL = run.direction === 'rtl';

    return (
        <React.Fragment>
            <style>
                {`
              @keyframes santaFlyLtr {
                from { transform: translateX(-160px) scaleX(1); }
                to   { transform: translateX(calc(100vw + 360px)) scaleX(1); }
              }
              @keyframes santaFlyRtl {
                from { transform: translateX(calc(100vw + 360px)) scaleX(-1); }
                to   { transform: translateX(-160px) scaleX(-1); }
              }
            `}
            </style>

            <Box
                key={run.id}
                sx={{
                    position: 'fixed',
                    left: 0,
                    top: '42px',
                    width: 'calc(100vw + 360px)',
                    marginLeft: '-160px',
                    pointerEvents: 'none',
                    zIndex: 1
                }}
            >
                <Box
                    component="img"
                    src="/assets/santa.gif"
                    alt="Santa"
                    onAnimationEnd={() => {
                        if (debug) {
                            scheduleRun(); // non-stop
                        } else {
                            setRun(null); // normal behavior
                        }
                    }}
                    sx={{
                        height: 30,
                        maxWidth: 90,
                        transformOrigin: 'center',
                        animationName: isRTL ? 'santaFlyRtl' : 'santaFlyLtr',
                        animationDuration: `${run.durationMs}ms`,
                        animationTimingFunction: 'linear',
                        animationIterationCount: 1
                    }}
                />
            </Box>
        </React.Fragment>
    );
};
